import { Drawer, IconButton, Paper, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import {
  AutocompleteInput,
  Button,
  ChipField,
  Confirm,
  Datagrid,
  DateField,
  FilterButton,
  FunctionField,
  List,
  NumberField,
  RaRecord,
  SearchInput,
  TextField,
  TopToolbar,
  useListContext,
  useNotify,
} from "react-admin";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { UsageRewardDetail } from "./Detail";
import { get, isEmpty } from "lodash";
import { DateRange } from "../DateRange";
import AddBoxIcon from "@mui/icons-material/AddBox";
import GetAppIcon from "@mui/icons-material/GetApp";
import { ManualRedemptionDialog } from "./components/ManualRedemptionDialog";
import { useRetrievePermission } from "src/hooks/useRetrievePermission";
import { exportRewardRedemptionHistory } from "./controller";

const PLATFORM_EXPORT_MAP: Record<string, string> = {
  GHK: "GHK",
  M21_HK: "M21",
  M21_TW: "M21",
};

// Remaps console filter keys to the backend export job's filter contract
// (design.md Decision 5) — dateGte/dateLte/code/platform don't share the
// backend's key names or, for platform, value shape.
export const remapRewardExportFilter = (
  filterValues: Record<string, any> = {}
): Record<string, any> => {
  const remapped: Record<string, any> = {};
  if (filterValues.dateGte) remapped.created_at_from = filterValues.dateGte;
  if (filterValues.dateLte) remapped.created_at_to = filterValues.dateLte;
  if (filterValues.code) remapped.booking_code = filterValues.code;
  if (filterValues.status) remapped.status = filterValues.status;
  if (filterValues.platform) {
    remapped.platform =
      PLATFORM_EXPORT_MAP[filterValues.platform] ?? filterValues.platform;
  }
  return remapped;
};

export const ExportRewardButton = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { filterValues } = useListContext();
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const handleClick = async () => {
    try {
      const result = await exportRewardRedemptionHistory(
        remapRewardExportFilter(filterValues)
      );
      if (result?.status) {
        setConfirmOpen(true);
      } else {
        notify(result?.error || "Export failed", { type: "error" });
      }
    } catch (error: any) {
      notify(error?.message || "Export failed", { type: "error" });
    }
  };

  const navigateToReportPage = () => {
    navigate(encodeURI(`/reports?filter={"type":"reward-redemption-history"}`));
  };

  return (
    <>
      <Button label="Export" onClick={handleClick}>
        <GetAppIcon />
      </Button>
      <Confirm
        isOpen={isConfirmOpen}
        title="Dear User,"
        content={
          <Paper elevation={0}>
            <Typography variant="body1" gutterBottom>
              Your file is exported, to view the progress please visit report
              page below
            </Typography>
          </Paper>
        }
        onClose={() => setConfirmOpen(false)}
        confirm="Go To Report"
        onConfirm={navigateToReportPage}
      />
    </>
  );
};

const rowStyleByStatus = (record: RaRecord | undefined) => {
  if (!record) return {};
  let bgColor = "transparent";
  if (record.status && typeof record.status === "string") {
    switch (record.status.toLowerCase()) {
      case "redeemed":
        bgColor = "#dcf8c6";
        break;
      case "expired":
        bgColor = "#ffe4e4";
        break;
      default:
        break;
    }
  }
  return {
    backgroundColor: bgColor,
  };
};

const ListActions = ({ filters }: any) => {
  const [isManualRedemptionOpen, setManualRedemptionOpen] = useState(false);
  const [isSuperAdmin, isAdmin, isStaff] = useRetrievePermission();
  const canManuallyRedeem = isSuperAdmin || isAdmin || isStaff;

  const handleOpenDialog = () => {
    setManualRedemptionOpen(true);
  };

  const handleCloseDialog = () => {
    setManualRedemptionOpen(false);
  };

  return (
    <>
      <TopToolbar style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {canManuallyRedeem && (
          <IconButton
            onClick={handleOpenDialog}
            color="primary"
            aria-label="Manual Order"
          >
            <AddBoxIcon />
          </IconButton>
        )}
        <FilterButton filters={filters} />
        <ExportRewardButton />
      </TopToolbar>
      <ManualRedemptionDialog
        open={isManualRedemptionOpen}
        onClose={handleCloseDialog}
      />
    </>
  );
};

const RewardFilters = [
  <DateRange alwaysOn />,
  <SearchInput source="code" />,
  <AutocompleteInput
    label="Status"
    source="status"
    choices={[
      { id: "PURCHASED", name: "PURCHASED" },
      { id: "REDEEMED", name: "REDEEMED" },
      { id: "EXPIRED", name: "EXPIRED" },
    ]}
    data-testid="search-status"
    sx={{
      minWidth: "250px",
    }}
  />,
  <AutocompleteInput
    label="Platform"
    source="platform"
    choices={[
      { id: "GHK", name: "GHK" },
      { id: "M21_HK", name: "M21 HK" },
      { id: "M21_TW", name: "M21 TW" },
    ]}
    data-testid="search-status"
    sx={{
      minWidth: "250px",
    }}
  />,
];

export const UsageRewardList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const match = matchPath("/usages/reward/:id/show/*", location.pathname);

  const handleCancelDrawer = useCallback(() => {
    navigate(`/usages/reward`);
  }, [navigate]);

  return (
    <>
      <List
        resource="rewardLogs"
        sort={{ field: "created_at", order: "DESC" }}
        title="Usages &rsaquo; Rewards"
        empty={false}
        filters={RewardFilters}
        disableSyncWithLocation
        actions={<ListActions filters={RewardFilters} />}
      >
        <Datagrid
          size="small"
          rowStyle={rowStyleByStatus}
          bulkActionButtons={false}
          rowClick={(id) => `/usages/reward/${id}/show`}
        >
          <DateField
            source="created_at"
            label="Date"
            showTime
            locales="en-HK"
            options={{
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              second: "numeric",
            }}
          />
          <FunctionField
            render={(record?: RaRecord) => {
              if (!record) return null;
              const subsInfo = get(record, "subscriber_info", {});
              if (!isEmpty(subsInfo)) {
                if (subsInfo.platform_info?.platform === "GHK") {
                  return "GHK";
                }
                return `${subsInfo.platform_info?.platform} ${subsInfo.subscriber.country}`;
              }
              return null;
            }}
            label="Platform"
            sortable={false}
          />
          <TextField source="code" label="Booking Code" />
          <ChipField
            sx={{ margin: 0 }}
            label="Status"
            size="small"
            source="status"
          />
          <FunctionField
            render={(record?: RaRecord) => {
              if (!record) return null;
              const rewardLabel = get(record, "reward_detail.item_label", {});
              const subsInfo = get(record, "subscriber_info.subscriber", {});
              return !isEmpty(subsInfo) ? rewardLabel[subsInfo.country] : null;
            }}
            label="Reward"
            sortable={false}
          />
          <TextField
            label="Subscriber"
            source="subscriber_info.subscriber.name"
            sortable={false}
          />
          <TextField source="reward_code.code" label="Item Code" />
          <NumberField
            label="Deducted Coin/Balance"
            source="subscriber_info.deducted_balance.coin"
            sortable={false}
          />
          <NumberField
            label="Deducted Point"
            source="subscriber_info.deducted_balance.point"
            sortable={false}
          />
        </Datagrid>
      </List>
      <Drawer
        variant="persistent"
        open={!!match}
        anchor="right"
        onClose={handleCancelDrawer}
        sx={{ zIndex: 100 }}
      >
        {!!match && (
          <UsageRewardDetail
            id={(match as any).params.id}
            onCancel={handleCancelDrawer}
          />
        )}
      </Drawer>
    </>
  );
};
